// src/components/TaskList.js
import { useState } from "react";
import { CATEGORIES, PRIORITIES, CAT_ICONS, PRIORITY_COLORS, PRIORITY_LIGHT } from "../constants";

export default function TaskList({ tasks, onSelect, onToggleComplete, onAdd, initialFilter }) {
  const [filterCategory, setFilterCategory] = useState("All");
  const [filterPriority, setFilterPriority] = useState("All");
  const [showCompleted, setShowCompleted] = useState(initialFilter === "completed");
  const [quickFilter, setQuickFilter] = useState(initialFilter || "all");

  function applyQuickFilter(tasks) {
    switch (quickFilter) {
      case "open":      return tasks.filter(t => !t.completed);
      case "overdue":   return tasks.filter(t => !t.completed && t.dueDate && new Date(t.dueDate) < new Date());
      case "urgent":    return tasks.filter(t => !t.completed && t.priority === "Urgent");
      case "completed": return tasks.filter(t => t.completed);
      default:          return tasks;
    }
  }

  const filtered = applyQuickFilter(tasks)
    .filter(t => {
      if (quickFilter === "all" && !showCompleted && t.completed) return false;
      if (filterCategory !== "All" && t.category !== filterCategory) return false;
      if (filterPriority !== "All" && t.priority !== filterPriority) return false;
      return true;
    })
    .sort((a, b) => {
      const pd = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      return pd[a.priority] - pd[b.priority];
    });

  return (
    <div className="fade-in">
      {/* Quick filter chips */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {[
          { key: "all",       label: "All" },
          { key: "open",      label: "Open" },
          { key: "overdue",   label: "Overdue" },
          { key: "urgent",    label: "Urgent" },
          { key: "completed", label: "Completed" },
        ].map(({ key, label }) => (
          <button key={key}
            onClick={() => { setQuickFilter(key); setShowCompleted(key === "completed"); }}
            style={{
              padding: "6px 16px", borderRadius: 20, border: "2px solid",
              cursor: "pointer", fontFamily: "'Nunito', sans-serif",
              fontSize: 13, fontWeight: 800, transition: "all 0.18s",
              borderColor: quickFilter === key ? "#f97316" : "#e8d5b8",
              background: quickFilter === key ? "#f97316" : "white",
              color: quickFilter === key ? "white" : "#a07850",
            }}
          >{label}</button>
        ))}
      </div>

      {/* Filters row with New Task button */}
      <div style={styles.filters}>
        <select className="field-input" style={{ width: "auto" }} value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
          <option value="All">All Categories</option>
          {CATEGORIES.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className="field-input" style={{ width: "auto" }} value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="All">All Priorities</option>
          {PRIORITIES.map(p => <option key={p}>{p}</option>)}
        </select>
        {quickFilter === "all" && (
          <label style={styles.checkLabel}>
            <input type="checkbox" checked={showCompleted} onChange={e => setShowCompleted(e.target.checked)} style={{ width: "auto" }} />
            Show Completed
          </label>
        )}
        <span style={styles.countLabel}>{filtered.length} task{filtered.length !== 1 ? "s" : ""}</span>
        <button className="btn-primary" onClick={onAdd} style={{ padding: "9px 18px", fontSize: 13, marginLeft: "auto" }}>
          + New Task
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
          <p style={{ fontWeight: 700, color: "#a07850", marginBottom: 20 }}>No tasks match your filters.</p>
          <button className="btn-primary" onClick={onAdd}>+ Add New Task</button>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          {filtered.map(task => (
            <div key={task.id} className="task-row" style={{ opacity: task.completed ? 0.55 : 1 }}>
              {/* Checkbox */}
              <div
                onClick={e => { e.stopPropagation(); onToggleComplete(task); }}
                style={{
                  width: 22, height: 22, borderRadius: 7, flexShrink: 0,
                  border: `2.5px solid ${task.completed ? "#22c55e" : "#d4a96a"}`,
                  background: task.completed ? "#22c55e" : "white",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", transition: "all 0.18s",
                }}
              >
                {task.completed && <span style={{ color: "white", fontSize: 13, fontWeight: 900 }}>✓</span>}
              </div>

              <span style={{ fontSize: 20, flexShrink: 0 }}>{CAT_ICONS[task.category]}</span>

              <div style={{ flex: 1, cursor: "pointer" }} onClick={() => onSelect(task)}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#2c1810", textDecoration: task.completed ? "line-through" : "none" }}>
                  {task.title}
                </div>
                <div style={{ fontSize: 12, color: "#a07850", fontWeight: 600, marginTop: 2 }}>
                  {task.category}
                  {task.dueDate ? ` · Due: ${task.dueDate}` : ""}
                  {task.recurrence && task.recurrence !== "None" ? ` · ${task.recurrence}` : ""}
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {task.images && task.images.length > 0 && <span title={`${task.images.length} photo(s)`}>📷</span>}
                {task.partsList && task.partsList.length > 0 && <span title="Has parts list">🛒</span>}
                {task.repairGuide && <span title="Has repair guide">📖</span>}
                <span style={{ background: PRIORITY_LIGHT[task.priority], color: PRIORITY_COLORS[task.priority], padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>
                  {task.priority}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  filters: { display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" },
  checkLabel: { display: "flex", alignItems: "center", gap: 8, color: "#7a5c3a", cursor: "pointer", fontSize: 14, fontWeight: 700, fontFamily: "'Nunito', sans-serif" },
  countLabel: { fontWeight: 800, color: "#a07850", fontSize: 14, fontFamily: "'Nunito', sans-serif" },
};
